import type { Action, PipelineSpec } from './pipeline-schema.ts';
import { ACTION_TYPES } from './pipeline-schema.ts';
import type { LeafFilter, Level1Group, RootFilter } from '#plugin-chat/lib/data-pipeline/filter/schema.ts';
import { InvalidSpecError } from '#plugin-chat/lib/tool/errors/index.ts';

/**
 * One node inside a {@link RootFilter}'s `filters` array — either a leaf
 * predicate or a level-1 group of leaves.
 */
type FilterNode = LeafFilter | Level1Group;

/**
 * Maps a column reference to (possibly) another string. Return the original
 * `ref` to leave the site unchanged; return a different string to rewrite.
 */
export type ColumnRefMapper = (ref: string) => string;

/**
 * Single source of truth for "which fields in a {@link PipelineSpec} are
 * column references." Walks every such site and applies `map`, returning a
 * new spec with rewrites applied.
 *
 * Adding a new action type only requires extending the per-action dispatch
 * in {@link mapAction}; downstream consumers (dotted-path materialisation,
 * inline-expression extraction, future column-rename refactors) pick up the
 * new site without their own parallel walk.
 *
 * Sites covered per action:
 * - `filter.where` — every leaf's `column`, recursively through level-1 groups
 * - `sort.keys[].column`
 * - `rollup.columns[]` and `rollup.aggregations[].column` (when present)
 * - `pivot.keyColumn` / `pivot.valueColumn`
 * - `fold.columns[]`
 * - `spread.column` / `unroll.column`
 *
 * Deliberately not walked:
 * - `derive.derives[].name` and `rollup.aggregations[].name` — output
 *   names, not refs.
 * - `derive.derives[].expression` — math.js text, not a single column ref.
 */
export function mapColumnRefs(spec: PipelineSpec, map: ColumnRefMapper): PipelineSpec {
  return { ...spec, actions: spec.actions.map((action) => mapAction(action, map)) };
}

/**
 * Read-only convenience over {@link mapColumnRefs}. Returns every column
 * reference encountered in spec-traversal order — composable with `.filter`
 * / `.map` for collectors that don't rewrite (e.g. dotted-path
 * materialisation in `collectDottedPathRefs`).
 *
 * Drives the same SSOT walker via an identity mapper that accumulates each
 * ref into the returned array — no parallel traversal logic to keep in lockstep.
 */
export function collectColumnRefs(spec: PipelineSpec): string[] {
  const refs: string[] = [];
  mapColumnRefs(spec, (ref) => {
    refs.push(ref);
    return ref;
  });
  return refs;
}

/**
 * Per-action column-ref rewriters. Each entry is keyed by `action.type`
 * and accepts the narrowed action shape for that key. Adding a new action
 * type means adding one entry here.
 */
const ACTION_MAPPERS: {
  [K in Action['type']]: (action: Extract<Action, { type: K }>, map: ColumnRefMapper) => Action;
} = {
  filter: (a, map) => ({ ...a, where: mapFilter(a.where, map) }),
  derive: (a) => a,
  sort: (a, map) => ({ ...a, keys: a.keys.map((k) => ({ ...k, column: map(k.column) })) }),
  slice: (a) => a,
  rollup: (a, map) => ({
    ...a,
    columns: a.columns?.map(map),
    aggregations: a.aggregations.map((agg) => (agg.column === undefined ? agg : { ...agg, column: map(agg.column) })),
  }),
  pivot: (a, map) => ({ ...a, keyColumn: map(a.keyColumn), valueColumn: map(a.valueColumn) }),
  fold: (a, map) => ({ ...a, columns: a.columns.map(map) }),
  spread: (a, map) => ({ ...a, column: map(a.column) }),
  unroll: (a, map) => ({ ...a, column: map(a.column) }),
  expandTimeseries: (a, map) => ({
    ...a,
    columns: a.columns.map(map),
    ...(a.splitBy === undefined ? {} : { splitBy: map(a.splitBy) }),
  }),
};

function mapAction(action: Action, map: ColumnRefMapper): Action {
  const mapper = ACTION_MAPPERS[action.type] as undefined | ((a: Action, m: ColumnRefMapper) => Action);
  if (mapper !== undefined) {
    return mapper(action, map);
  }

  // Unknown / missing action type. Two distinct LLM mistakes hit this path:
  //
  // 1. **Missing-type tagged-format leftover**: the LLM emitted a single-key
  //    object like `{ joinDatasets: {...} }` (the tagged-union shape used in
  //    JSON Schema for LLM input). `convertTaggedToDiscriminated` only
  //    rewrites tagged keys that match a registered discriminator value;
  //    keys that don't match a known action type pass through unchanged,
  //    leaving `action.type === undefined`. Diagnose this case explicitly
  //    so the LLM knows it tried to invoke a top-level tool inside a
  //    transform pipeline (joinDatasets, queryKnowledgeGraph, etc.).
  //
  // 2. **Wrong literal type**: the LLM emitted `{ type: 'wat', ... }` with
  //    a literal type string that isn't one of {@link ACTION_TYPES}.
  //
  // Without this throw, the optional-chain on `mapper` would silently
  // produce `undefined`, and `transformDataset`'s reduce loop would crash
  // downstream with "can't access property 'type', action is undefined".
  const rawType = (action as { type?: unknown }).type;
  const actionKeys = Object.keys((action ?? {}) as Record<string, unknown>);
  const looksLikeTaggedLeftover = rawType === undefined && actionKeys.length === 1;
  const taggedKey = looksLikeTaggedLeftover ? actionKeys[0] : undefined;

  if (looksLikeTaggedLeftover && taggedKey !== undefined) {
    throw new InvalidSpecError(
      `Action "${taggedKey}" is not a valid transform action — it is either a separate top-level tool or an unknown name.`,
      {
        suggestions: [
          `Valid transform actions are: ${ACTION_TYPES.join(', ')}.`,
          `If "${taggedKey}" is a separate tool (e.g. joinDatasets, queryKnowledgeGraph, registerDataset), invoke it as its own tool call — not as an action inside transformDataset.`,
          'Use the discriminated form `{ type: "<actionType>", ... }`, not the tagged form `{ <actionType>: {...} }`.',
        ],
        details: { invalidActionTag: taggedKey, validTypes: ACTION_TYPES },
      },
    );
  }

  throw new InvalidSpecError(`Unknown action type "${String(rawType)}". Valid types: ${ACTION_TYPES.join(', ')}.`, {
    suggestions: [
      `Use one of the supported action types: ${ACTION_TYPES.join(', ')}.`,
      'For joining two datasets, call the `joinDatasets` tool — it is a separate top-level tool, not a transform action.',
    ],
    details: { unknownType: rawType, validTypes: ACTION_TYPES },
  });
}

function mapFilter(node: RootFilter, map: ColumnRefMapper): RootFilter {
  return { ...node, filters: node.filters.map((child) => mapFilterNode(child, map)) };
}

function mapFilterNode(node: FilterNode, map: ColumnRefMapper): FilterNode {
  // Leaf predicates carry the column ref directly. Level-1 groups carry a
  // nested `filters` array of leaves only (no further nesting per schema), so
  // the recursion bottoms out one level deeper.
  if ('column' in node) {
    return { ...node, column: map(node.column) };
  }
  return {
    ...node,
    filters: node.filters.map((leaf) => ({ ...leaf, column: map(leaf.column) })),
  };
}
